<template>
  <div class="client-services-content">
    <v-container fluid>
      <v-row align="center" class="px-2">
        <v-col cols="auto">
          <v-btn text icon @click="refresh" v-if="!isVisible">
            <v-icon color="cyan darken-2">refresh</v-icon>
          </v-btn>
          <div v-if="isVisible" class="load-3 ml-2">
            <div class="line"></div>
            <div class="line"></div>
            <div class="line"></div>
          </div>
        </v-col>

        <v-spacer></v-spacer>

        <v-col cols="12" sm="6" md="5" class="d-flex align-center">
          <v-autocomplete
            label="Select Service to Add"
            :items="availableServices"
            v-model="selectedService"
            item-value="id"
            item-text="display"
            hide-details
            dense
            outlined
            class="mr-2"
          ></v-autocomplete>
          <v-tooltip top color="secondary">
            <template v-slot:activator="{ on }">
              <v-btn class="mx-2" x-small fab dark color="blue lighten-1" @click="addServiceRow"
                     v-on="on">
                <v-icon dark>add</v-icon>
              </v-btn>
            </template>
            <span>add client service</span>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="localLoading"
        item-key="id"
        class="elevation-1 mt-4"
      >
        <template v-slot:item.name="{ item }">
          {{ item.id }} - {{ item.name }}
        </template>

        <template v-slot:item.sms_silent="{ item }">
          <span v-if="item.sms_silent_use">Yes ({{ item.sms_silent_start }} - {{ item.sms_silent_end }})</span>
          <span v-else>No</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip top v-if="!isNewClient">
            <template v-slot:activator="{ on }">
              <v-btn icon color="blue" @click="openSettingsDialog(item)" v-on="on">
                <v-icon small>edit</v-icon>
              </v-btn>
            </template>
            <span>edit service settings</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon color="red" @click="removeService(item)"
                     :disabled="item.id === 1 || isPending(item)" v-on="on">
                <v-icon small>delete</v-icon>
              </v-btn>
            </template>
            <span v-if="item.id === 1">the standard service cannot be removed</span>
            <span v-else-if="isPending(item)">will not be saved with the client</span>
            <span v-else>remove client service</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <div v-if="isNewClient" class="mt-2 text-caption grey--text">
        Services added here will be saved with the client. Service settings can be edited after the client is created.
      </div>

      <v-divider></v-divider>

      <v-dialog v-model="settingsDialog" width="700px" persistent>
        <v-card v-if="settingsItem">
          <v-card-title class="dialog-title">
            <span class="text-h6">Service Settings: {{ settingsItem.id }} - {{ settingsItem.name }}</span>
          </v-card-title>
          <v-card-text>
            <v-container container--fluid grid-list-md>
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    outlined
                    label="Price Multiplier"
                    :persistent-hint="canEditPricing"
                    :hint="canEditPricing ? 'Leave blank to use the service default' : 'Requires client pricing edit permission'"
                    :disabled="!canEditPricing"
                    v-model="settingsForm.price_multiplier"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-menu v-model="menuCutoff" :close-on-content-click="false" transition="scale-transition" offset-y>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        outlined
                        label="Insert Cutoff Time"
                        hint="Leave blank to use the client default"
                        persistent-hint
                        v-model="settingsForm.insert_cutoff_time"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker v-if="menuCutoff" format="24hr" v-model="settingsForm.insert_cutoff_time" @change="menuCutoff = false"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-checkbox
                    label="Use Silent Window"
                    v-model="settingsForm.sms_silent_use"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" offset-y>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        outlined
                        label="Silent Start"
                        :disabled="!settingsForm.sms_silent_use"
                        v-model="settingsForm.sms_silent_start"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker v-if="menuStart" format="24hr" v-model="settingsForm.sms_silent_start" @change="menuStart = false"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" offset-y>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        outlined
                        label="Silent End"
                        :disabled="!settingsForm.sms_silent_use"
                        v-model="settingsForm.sms_silent_end"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker v-if="menuEnd" format="24hr" v-model="settingsForm.sms_silent_end" @change="menuEnd = false"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    counter="255"
                    :rules="[v => v === null || v === undefined || v.length <= 255 || 'Message must be at most 255 characters']"
                    label="Tracking Message"
                    hint="Leave blank to use the client/service default"
                    persistent-hint
                    v-model="settingsForm.sms_message_tracking"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    counter="255"
                    :rules="[v => v === null || v === undefined || v.length <= 255 || 'Message must be at most 255 characters']"
                    label="Transaction Message"
                    hint="Leave blank to use the client/service default"
                    persistent-hint
                    v-model="settingsForm.sms_message_transaction"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    counter="255"
                    :rules="[v => v === null || v === undefined || v.length <= 255 || 'Message must be at most 255 characters']"
                    label="Pre-transaction Message"
                    hint="Leave blank to use the client/service default"
                    persistent-hint
                    v-model="settingsForm.sms_message_pre_transaction"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    counter="255"
                    :rules="[v => v === null || v === undefined || v.length <= 255 || 'Message must be at most 255 characters']"
                    label="Attempted Delivery Message"
                    hint="Leave blank to use the service default"
                    persistent-hint
                    v-model="settingsForm.sms_message_attempted_delivery"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="justify-center pb-4">
            <v-btn outlined color="grey darken-1" height="40" class="mr-4" @click="closeSettingsDialog">Cancel</v-btn>
            <v-btn color="primary" height="40" :loading="savingSettings" @click="saveSettings">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
// @group EditClient
// Client service types section for EditClient
// Manages the services attached to a client (client_services) and the
// per client service settings (client_services_settings).
import axios from "axios";

export default {
  name: "ClientServices",
  props: {
    // Client Object
    clientInfo: Object,
  },
  data() {
    return {
      allServices: [],
      clientServices: [],
      localLoading: false,
      isVisible: false,
      selectedService: null,
      pendingClientServices: [],
      settingsDialog: false,
      settingsItem: null,
      savingSettings: false,
      menuStart: false,
      menuEnd: false,
      menuCutoff: false,
      settingsForm: {
        price_multiplier: null,
        insert_cutoff_time: null,
        sms_silent_use: false,
        sms_silent_start: null,
        sms_silent_end: null,
        sms_message_tracking: null,
        sms_message_transaction: null,
        sms_message_pre_transaction: null,
        sms_message_attempted_delivery: null,
      },
      headers: [
        {text: 'ID', align: 'center', sortable: true, value: 'id', width: '80px'},
        {text: 'Service', align: 'center', sortable: true, value: 'name'},
        {text: 'Price Multiplier', align: 'center', sortable: true, value: 'price_multiplier', width: '140px'},
        {text: 'Insert Cutoff', align: 'center', sortable: true, value: 'insert_cutoff_time', width: '120px'},
        {text: 'SMS Silent Window', align: 'center', sortable: false, value: 'sms_silent', width: '160px'},
        {text: 'Action', align: 'center', sortable: false, value: 'actions', width: '120px'},
      ],
    }
  },
  computed: {
    isNewClient() {
      return !this.clientInfo || !this.clientInfo.id || this.clientInfo.id === 0;
    },
    canEditPricing() {
      return this.$store.getters['auth/hasPermission']('client_pricing_edit');
    },
    rows() {
      const pendingRows = this.pendingClientServices.map(service => ({
        id: service.id,
        name: service.name,
        price_multiplier: null,
        insert_cutoff_time: null,
        sms_silent_use: false,
        sms_silent_start: null,
        sms_silent_end: null,
      }));
      return [...this.clientServices, ...pendingRows];
    },
    availableServices() {
      const existingIds = this.rows.map(row => row.id);
      return this.allServices
        .filter(service => !existingIds.includes(service.id))
        .map(service => ({id: service.id, display: service.id + ' - ' + service.name}));
    },
  },
  methods: {
    isPending(item) {
      return this.pendingClientServices.some(service => service.id === item.id);
    },
    refresh() {
      this.isVisible = true;
      this.load().finally(() => {
        setTimeout(() => this.isVisible = false, 1000);
      });
    },
    load() {
      if (this.isNewClient) {
        this.clientServices = [];
        return Promise.resolve();
      }
      this.localLoading = true;
      return Promise.all([
        axios.get(process.env.API_URL + '/clients/' + this.clientInfo.id + '/services?with_settings=1')
          .then((response) => {
            this.clientServices = response.data || [];
          })
          .catch((error) => {
            console.log(error.response ? error.response.data : error);
            this.clientServices = [];
          }),
        axios.get(process.env.API_URL + '/services')
          .then((response) => {
            this.allServices = response.data || [];
          })
          .catch((error) => {
            console.log(error.response ? error.response.data : error);
            this.allServices = [];
          }),
      ]).finally(() => {
        this.localLoading = false;
      });
    },
    addServiceRow() {
      if (!this.selectedService) {
        this.$store.commit('displaySnackbar', 'Please select a service to add');
        return;
      }
      const service = this.allServices.find(item => item.id === this.selectedService);

      // If creating new client, store locally
      if (this.isNewClient) {
        if (this.isPending({id: this.selectedService})) {
          this.$store.commit('displaySnackbar', 'Service already added');
          return;
        }
        this.pendingClientServices.push({id: service.id, name: service.name});
        this.$store.commit('displaySnackbar', 'Service added (will be saved with client)');
        this.selectedService = null;
        return;
      }

      axios.post(process.env.API_URL + '/clients/' + this.clientInfo.id + '/services/' + this.selectedService)
        .then(() => {
          this.$store.commit('displaySnackbar', 'Service added');
          this.selectedService = null;
          this.load();
        })
        .catch((error) => {
          this.$store.commit('displaySnackbar', 'Error: ' + (error.response && error.response.data && error.response.data.error ? error.response.data.error : error));
        });
    },
    removeService(item) {
      if (item.id === 1) return;

      // If it is a pending service (not yet saved), just remove it locally
      if (this.isPending(item)) {
        const index = this.pendingClientServices.findIndex(service => service.id === item.id);
        if (index >= 0) {
          this.pendingClientServices.splice(index, 1);
          this.$store.commit('displaySnackbar', 'Service removed');
        }
        return;
      }

      axios.delete(process.env.API_URL + '/clients/' + this.clientInfo.id + '/services/' + item.id)
        .then(() => {
          this.$store.commit('displaySnackbar', 'Service removed');
          this.load();
        })
        .catch((error) => {
          this.$store.commit('displaySnackbar', 'Error: ' + (error.response && error.response.data && error.response.data.error ? error.response.data.error : error));
        });
    },
    openSettingsDialog(item) {
      this.settingsItem = item;
      this.settingsForm = {
        price_multiplier: item.price_multiplier === null || item.price_multiplier === undefined ? null : String(item.price_multiplier),
        insert_cutoff_time: item.insert_cutoff_time === null || item.insert_cutoff_time === undefined ? null : item.insert_cutoff_time,
        sms_silent_use: !!item.sms_silent_use,
        sms_silent_start: item.sms_silent_start === null || item.sms_silent_start === undefined ? null : item.sms_silent_start,
        sms_silent_end: item.sms_silent_end === null || item.sms_silent_end === undefined ? null : item.sms_silent_end,
        sms_message_tracking: item.sms_message_tracking === null || item.sms_message_tracking === undefined ? null : item.sms_message_tracking,
        sms_message_transaction: item.sms_message_transaction === null || item.sms_message_transaction === undefined ? null : item.sms_message_transaction,
        sms_message_pre_transaction: item.sms_message_pre_transaction === null || item.sms_message_pre_transaction === undefined ? null : item.sms_message_pre_transaction,
        sms_message_attempted_delivery: item.sms_message_attempted_delivery === null || item.sms_message_attempted_delivery === undefined ? null : item.sms_message_attempted_delivery,
      };
      this.settingsDialog = true;
    },
    closeSettingsDialog() {
      this.settingsDialog = false;
      this.settingsItem = null;
    },
    saveSettings() {
      if (!this.settingsItem) return;

      const price = this.settingsForm.price_multiplier;
      if (price !== null && price !== undefined && price !== '' && (isNaN(parseFloat(price)) || parseFloat(price) < 0)) {
        this.$store.commit('displaySnackbar', 'Price multiplier must be a non-negative number');
        return;
      }

      const body = {
        insert_cutoff_time: this.settingsForm.insert_cutoff_time ? this.settingsForm.insert_cutoff_time : null,
        sms_silent_use: this.settingsForm.sms_silent_use ? true : false,
        sms_silent_start: this.settingsForm.sms_silent_start ? this.settingsForm.sms_silent_start : null,
        sms_silent_end: this.settingsForm.sms_silent_end ? this.settingsForm.sms_silent_end : null,
        sms_message_tracking: this.settingsForm.sms_message_tracking ? this.settingsForm.sms_message_tracking : null,
        sms_message_transaction: this.settingsForm.sms_message_transaction ? this.settingsForm.sms_message_transaction : null,
        sms_message_pre_transaction: this.settingsForm.sms_message_pre_transaction ? this.settingsForm.sms_message_pre_transaction : null,
        sms_message_attempted_delivery: this.settingsForm.sms_message_attempted_delivery ? this.settingsForm.sms_message_attempted_delivery : null,
      };

      this.savingSettings = true;
      axios.put(process.env.API_URL + '/clients/' + this.clientInfo.id + '/services/' + this.settingsItem.id, body)
        .then((response) => {
          const index = this.clientServices.findIndex(service => service.id === this.settingsItem.id);
          if (index >= 0 && response.data) {
            this.clientServices.splice(index, 1, response.data);
          }
          this.$store.commit('displaySnackbar', 'Service settings saved');
          this.closeSettingsDialog();
        })
        .catch((error) => {
          this.$store.commit('displaySnackbar', 'Error: ' + (error.response && error.response.data && error.response.data.error ? error.response.data.error : error));
        })
        .finally(() => {
          this.savingSettings = false;
        });
    },
    // @vuese
    // Get pending client services for new client creation
    getPendingClientServices() {
      return this.pendingClientServices;
    },
  },
  watch: {
    'clientInfo.id': {
      immediate: true,
      handler() {
        this.load();
      }
    },
  },
}
</script>

<style scoped>
.client-services-content {}

.v-text-field,
.v-select,
.v-autocomplete {
  margin-bottom: 8px;
}

.v-text-field.v-text-field--outlined >>> .v-input__control,
.v-select.v-select--outlined >>> .v-input__control,
.v-autocomplete.v-autocomplete--outlined >>> .v-input__control {
  min-height: 56px;
}

.v-text-field.v-text-field--outlined >>> .v-input__slot,
.v-select.v-select--outlined >>> .v-input__slot,
.v-autocomplete.v-autocomplete--outlined >>> .v-input__slot {
  min-height: 48px;
}
</style>



// WEBPACK FOOTER //
// src/components/util/Clients/ClientServices.vue